import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Typography, useTheme, IconButton } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import i18n from 'i18n-js';
import { useDispatch } from 'react-redux';
import { useStyles } from '../styles';

interface FileUploadDnDProps {
    onFileSelect?: (file: File) => void;
    onUpload?: (file: File) => void;
    maxSizeInMB?: number;
}

const FileUploadDnD: React.FC<FileUploadDnDProps> = ({
    onFileSelect,
    onUpload,
    maxSizeInMB = 5,
}) => {
    const classes = useStyles();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [error, setError] = useState<string>('');

    const validateFile = useCallback(
        (file: File): string => {
            // Check file type
            const validExtensions = ['.xlsx', '.xls'];
            const fileName = file.name.toLowerCase();
            const isValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

            if (!isValidExtension) {
                return i18n.t('vouchers.onlyXLSXFiles');
            }

            // Check file size
            const fileSizeInMB = file.size / (1024 * 1024);
            if (fileSizeInMB > maxSizeInMB) {
                return i18n.t('vouchers.fileTooLarge', {
                    maxSize: maxSizeInMB,
                });
            }

            return '';
        },
        [maxSizeInMB]
    );

    const handleFileSelection = useCallback(
        (file: File) => {
            const errorMessage = validateFile(file);
            setError(errorMessage);

            if (!errorMessage) {
                setSelectedFile(file);
                if (onFileSelect) {
                    onFileSelect(file);
                }
            } else {
                setSelectedFile(null);
            }
        },
        [validateFile, onFileSelect]
    );

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            if (!!onUpload) {
                setIsDragOver(true);
            }
        },
        [onUpload]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            if (!!!onUpload) return;

            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                handleFileSelection(files[0]);
            }
        },
        [onUpload, handleFileSelection]
    );

    const handleClick = useCallback(() => {
        if (!!onUpload && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [onUpload]);

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                handleFileSelection(files[0]);
            }
            // Reset input value to allow selecting the same file again
            e.target.value = '';
        },
        [handleFileSelection]
    );

    const handleUpload = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            if (e) {
                e.stopPropagation();
            }
            if (selectedFile && onUpload) {
                onUpload(selectedFile);
                setTimeout(() => handleRemoveFile(), 2000);
            }
        },
        [selectedFile, onUpload]
    );

    const handleRemoveFile = useCallback((e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setSelectedFile(null);
        setError('');
        // Reset the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    return (
        <Box>
            <Box
                className={`${classes.uploadContainer} ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <CloudUploadIcon className={classes.uploadIcon} />
                <Box className={classes.dataContainer}>
                    {selectedFile ? (
                        <Box className={classes.fileName}>
                            <Typography component="span">
                                {i18n.t('vouchers.selectedFile')}: {selectedFile.name}
                            </Typography>
                            <IconButton
                                size="small"
                                className={classes.removeFileButton}
                                onClick={handleRemoveFile}
                                disabled={!!!selectedFile}
                                title={i18n.t('common.remove')}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box>
                            <Typography className={classes.uploadText}>
                                {i18n.t('vouchers.dragDropText')}
                            </Typography>

                            <Typography className={classes.supportedText}>
                                {i18n.t('vouchers.supportedFormat', {
                                    maxSize: maxSizeInMB,
                                })}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Button
                    variant="contained"
                    className={classes.uploadButton}
                    disabled={!!!selectedFile || !!!onUpload}
                    onClick={handleUpload}
                >
                    {i18n.t('vouchers.uploadFile')}
                </Button>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className={classes.hiddenInput}
                />
                {error && <Typography className={classes.errorText}>{error}</Typography>}
            </Box>
        </Box>
    );
};

export default FileUploadDnD;
